<template>
  <div class="width-100p">
    <v-btn
      depressed
      large
      block
      color="white"
      class="ma-0"
      @click="dialog = true"
    >
      Edit User
    </v-btn>

    <v-dialog
      v-model="dialog"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 class="text-h5">Edit user</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog()"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <div v-if="loading" class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <ValidationObserver v-else ref="observer">
            <v-form>
              <v-row>
                <v-col cols="6" class="pb-0">
                  <ValidationProvider
                    id="first_names"
                    v-slot="{ errors }"
                    name="first_names"
                    rules="required"
                  >
                    <v-text-field
                      v-model.trim="user.firstNames"
                      label="First names*"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="6" class="pb-0">
                  <ValidationProvider
                    id="last_name"
                    v-slot="{ errors }"
                    name="last_name"
                    rules="required"
                  >
                    <v-text-field
                      v-model.trim="user.lastName"
                      label="Last name*"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <ValidationProvider
                v-if="whitelabel.authentication.signUp.showEmail"
                id="email"
                v-slot="{ errors }"
                name="email"
                rules="required|email"
              >
                <v-text-field
                  v-model.trim="user.email"
                  label="Email*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="whitelabel.authentication.signUp.showPhone"
                id="phone"
                v-slot="{ errors }"
                name="phone"
                :rules="{
                  phoneIntl: [user.testator.cellCountryCode],
                }"
              >
                <v-text-field
                  v-model.trim="user.phone"
                  label="Phone*"
                  :error-messages="errors"
                  @input="handlePhoneInput"
                  :messages="phoneMessages"
                  :readonly="user.phone === user.testator.cellNumber"
                />
              </ValidationProvider>
              <ValidationProvider
                v-if="whitelabel.authentication.signUp.showUniqueIdentifier"
                id="unique_identifier"
                v-slot="{ errors }"
                name="unique_identifier"
                rules="required"
              >
                <v-text-field
                  v-model.trim="user.uniqueIdentifier"
                  label="Industry Number*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                id="role"
                v-slot="{ errors }"
                name="role"
                rules="required"
              >
                <v-select
                  v-model="user.role"
                  :items="$options.roles"
                  label="Role*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isLeadProvider"
                id="lead_provider_code"
                v-slot="{ errors }"
                name="lead_provider_code"
              >
                <v-text-field
                  v-model.trim="user.managerEmail"
                  label="Manager email address*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="showSppLeadProvider"
                id="spp_lead_provider"
                v-slot="{ errors }"
                name="spp_lead_provider"
              >
                <label class="text-subtitle-1"
                  >Should this user have access to create DFA SPP leads?</label
                >
                <v-checkbox
                  v-model="user.sppProvider"
                  label="SPP Lead Provider"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="selectedUserIsAdvisor"
                id="advisor_code"
                v-slot="{ errors }"
                name="advisor_code"
              >
                <v-text-field
                  v-model.trim="advisorCode"
                  label="Advisor code"
                  :error-messages="errors"
                  :disabled="!!user.advisorCode"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="selectedUserRole || selectedUserIsAdmin"
                id="external_reference"
                v-slot="{ errors }"
                name="external_reference"
                :rules="selectedUserRole ? 'required' : ''"
              >
                <v-text-field
                  v-model.trim="user.externalReference"
                  :label="
                    selectedUserRole
                      ? 'External reference*'
                      : 'External reference'
                  "
                  :hint="
                    selectedUserIsAdmin
                      ? 'Needed to start DifferentLife sales processes'
                      : ''
                  "
                  persistent-hint
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isCallCenterAgentOrConsultant"
                id="system_wide_access"
                v-slot="{ errors }"
                name="system_wide_access"
                rules="required"
              >
                <label class="text-subtitle-1"
                  >Should this call center agent have access to all companies or
                  only the company assigned below?*</label
                >
                <v-radio-group
                  v-model="user.systemWideAccess"
                  row
                  class="mt-2"
                  :error-messages="errors"
                >
                  <v-radio
                    label="System Wide Access"
                    :value="true"
                    color="primary"
                  />
                  <v-radio
                    label="Company Specific Access"
                    :value="false"
                    color="primary"
                  />
                </v-radio-group>
              </ValidationProvider>

              <ValidationProvider
                v-if="showCompaniesSelect"
                id="company"
                v-slot="{ errors }"
                name="company"
                rules="required"
              >
                <v-select
                  v-model="user.companyId"
                  :items="companies"
                  item-value="id"
                  item-text="tradingName"
                  label="Company*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="selectedUserIsAdvisor && showCompaniesSelect"
                id="company_wide_access"
                v-slot="{ errors }"
                name="company_wide_access"
                rules="required"
              >
                <v-select
                  v-model="user.companyWideAccess"
                  :items="accessLevelOptions"
                  item-value="value"
                  item-text="text"
                  label="Access level*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="showBranchesSelect"
                id="branch"
                v-slot="{ errors }"
                name="branch"
                rules="required"
              >
                <v-select
                  v-model="user.branchId"
                  :items="companyBranches"
                  item-value="id"
                  item-text="code"
                  label="Branch*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="showAppointmentLink"
                id="appointment_link"
                v-slot="{ errors }"
                name="appointment_link"
              >
                <v-text-field
                  id="appointment_link_input"
                  v-model.trim="user.appointmentLink"
                  :error-messages="errors"
                  label="Appointment Link"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="showAdvisorEmailField"
                id="advisor_email"
                v-slot="{ errors }"
                name="advisor_email"
                rules="email"
              >
                <v-text-field
                  v-model.trim="advisorEmail"
                  label="Advisor email (leave blank to remove advisor)"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <v-select
                v-if="isAdmin && user.role != 'TESTATOR'"
                v-model="commissionScheme"
                :items="commissionSchemeOptions"
                item-text="text"
                item-value="value"
                label="Commission Scheme (override)"
                hint="Leave blank to inherit the company's scheme."
                persistent-hint
                clearable
                class="mb-4"
              />
              <object-commission-structure
                v-if="isAdmin && user.role != 'TESTATOR'"
                object_type="User"
                :object_id="user.id"
                :mobile="true"
                :show-inheritance="true"
                :initial-commission-structures="
                  user.objectCommissionPairs || []
                "
                :user-selected-commission-structure="user.commissionStructure"
                :commission-structure-code="user.commissionStructureCode"
                @commission-data-updated="handleCommissionDataUpdate"
                @selected-commission-structure="
                  handleSelectedCommissionStructure
                "
                @commission-structure-code="handleCommissionStructureCode"
              />
            </v-form>
          </ValidationObserver>
        </v-card-text>

        <v-card-actions class="crud-card-actions">
          <v-btn
            class="submit-button left-edit-btn"
            :loading="buttonLoading"
            rounded
            depressed
            large
            color="primary"
            @click="submit()"
          >
            <span>Update user</span>
            <v-icon large>{{ icons.mdiArrowRight }}</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </div>
</template>

<script>
  import { EDIT_USER_QUERY } from '../../../constants/graphql/users';
  import { UPDATE_USER_MUTATION } from '../../../constants/graphql/users';
  import ErrorSnackbar from '../../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../../mixins/form-helper-mixin';
  import SuccessSnackbar from '../../common/SuccessSnackbar.vue';
  import { commissionSchemeOptions } from '../../../constants/js/commission-schemes';
  import { COMPANY_BRANCHES_QUERY } from '../../../constants/graphql/branches';
  import { SIMPLE_COMPANIES_QUERY } from '../../../constants/graphql/companies';
  import ObjectCommissionStructure from '../commission/ObjectCommissionStructure.vue';

  export default {
    name: 'EditUserDialog',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
      ObjectCommissionStructure,
    },
    mixins: [formHelperMixin],
    props: {
      id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        user: {
          companyWideAccess: false,
        },
        accessLevelOptions: [
          { text: 'Company Wide Access', value: true },
          { text: 'Default Access', value: false },
        ],
        advisorEmail: null,
        companies: null,
        loading: true,
        dialog: false,
        showError: false,
        errorMessage: null,
        showSuccess: false,
        successMessage: null,
        companyBranches: [],
        commissionData: {
          objectCommissionPairs: [],
          commissionStructure: 'NONE',
        },
        advisorCode: null,
        commissionStructureCode: null,
        commissionScheme: null,
        commissionSchemeOptions,
        phoneMessages: [],
      };
    },
    computed: {
      selectedUserRole() {
        return this.user.role == 'CALL_CENTER_AGENT';
      },
      selectedUserIsAdvisor() {
        return this.user.role == 'ADVISOR';
      },
      selectedUserIsAdmin() {
        return this.user.role == 'ADMIN';
      },
      isLeadProvider() {
        return this.user.role == 'LEAD_PROVIDER';
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      showSppLeadProvider() {
        return ['ADMIN', 'CALL_CENTER_AGENT', 'ADVISOR'].includes(
          this.user.role
        );
      },
      isCallCenterAgentOrConsultant() {
        return (
          this.user.role == 'CALL_CENTER_AGENT' ||
          this.user.role == 'FIDUCIARY_CONSULTANT'
        );
      },
      showCompaniesSelect() {
        return (
          this.user.role == 'ADVISOR' ||
          this.user.role == 'COMPANY_ADMIN' ||
          this.user.role == 'FIDUCIARY_CONSULTANT' ||
          this.user.role == 'CALL_CENTER_AGENT' ||
          this.user.role == 'BRANCH_AGENT' ||
          this.user.role == 'LEAD_PROVIDER'
        );
      },
      showAccessLevelSelect() {
        return this.user.role == 'ADVISOR';
      },
      showBranchesSelect() {
        return this.user.role === 'BRANCH_AGENT';
      },
      showAppointmentLink() {
        return (
          this.user.role === 'ADVISOR' &&
          this.companies?.find(company => company.id === this.user.companyId)
            ?.advisorAppointment
        );
      },
      showAdvisorEmailField() {
        return this.user.role == 'TESTATOR';
      },
    },
    roles: [
      { text: 'Testator', value: 'TESTATOR' },
      { text: 'Advisor', value: 'ADVISOR' },
      { text: 'Admin', value: 'ADMIN' },
      { text: 'Company admin', value: 'COMPANY_ADMIN' },
      { text: 'Fiduciary consultant', value: 'FIDUCIARY_CONSULTANT' },
      { text: 'Call center agent', value: 'CALL_CENTER_AGENT' },
      { text: 'Branch agent', value: 'BRANCH_AGENT' },
      { text: 'Lead provider', value: 'LEAD_PROVIDER' },
    ],
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateUser();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      externalReferenceUpdateValue() {
        if (
          this.user.role === 'CALL_CENTER_AGENT' ||
          this.user.role === 'BRANCH_AGENT' ||
          this.user.role === 'ADMIN'
        ) {
          return this.user.externalReference;
        }

        return '';
      },
      advisorCodeUpdateValue() {
        if (this.user.role === 'ADVISOR') {
          return this.advisorCode;
        }

        return '';
      },

      updateUser() {
        const userInputDetails = {
          firstNames: this.user.firstNames,
          lastName: this.user.lastName,
          email: this.user.email,
          phone: this.user.phone,
          uniqueIdentifier: this.user.uniqueIdentifier,
          role: this.user.role,
          appointmentLink: this.user.appointmentLink,
          externalReference: this.externalReferenceUpdateValue(),
          advisorCode: this.advisorCodeUpdateValue(),
          systemWideAccess: this.user.systemWideAccess,
          companyWideAccess: this.user.companyWideAccess,
          sppProvider: this.user.sppProvider,
          managerEmail: this.user.managerEmail,
          objectCommissionPairsAttributes:
            this.commissionData.objectCommissionPairs,
          commissionStructure: this.user.commissionStructure,
          commissionStructureCode: this.commissionStructureCode,
          commissionScheme: this.commissionScheme,
        };
        userInputDetails['companyId'] = this.showCompaniesSelect
          ? this.user.companyId
          : null;
        userInputDetails['branchId'] = this.showBranchesSelect
          ? this.user.branchId
          : null;
        userInputDetails['advisorEmail'] = this.showAdvisorEmailField
          ? this.advisorEmail
          : null;
        const id = this.user.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_MUTATION,
            variables: {
              id,
              userInputDetails,
            },
          })
          .then(_response => {
            this.showSuccessMessage();
          })
          .catch(error => {
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
              this.displayErrorAndCloseDialog();
            } else {
              this.displayErrorAndCloseDialog();
              throw error;
            }
          });
      },
      showSuccessMessage() {
        this.successMessage = 'User has been updated successfully';
        this.showSuccess = true;
        this.$emit('userUpdated');
        this.$apollo.queries.user.refetch();
        this.closeDialog();
      },
      closeDialog() {
        this.dialog = false;
        this.buttonLoading = false;
      },
      handleCommissionDataUpdate(data) {
        this.commissionData = data;
      },
      handleSelectedCommissionStructure(value) {
        this.user.commissionStructure = value;
      },
      handleCommissionStructureCode(code) {
        this.commissionStructureCode = code;
      },
      handlePhoneInput(value) {
        if (value === '') {
          this.user.phone = this.user.testator.cellNumber;
          this.showMessage();
          setTimeout(() => {
            this.clearMessage();
          }, 3000);
        }
      },
      showMessage() {
        this.phoneMessages = ['Phone number copied from testator'];
      },
      clearMessage() {
        this.phoneMessages = [];
      },
    },
    watch: {
      'user.companyId': async function (newValue) {
        if (!newValue) {
          return;
        }

        try {
          const response = await this.$apollo.query({
            query: COMPANY_BRANCHES_QUERY,
            variables: {
              id: newValue,
            },
            fetchPolicy: 'network-only',
          });
          this.companyBranches = response.data.companyBranches;
        } catch (error) {
          console.error('Error fetching company branches:', error);
        }
      },
    },
    apollo: {
      companies: {
        query: SIMPLE_COMPANIES_QUERY,
      },
      user: {
        query: EDIT_USER_QUERY,
        variables() {
          return {
            id: this.id,
          };
        },
        result(response) {
          if (response.data && response.data.user) {
            this.advisorCode = this.user.advisorCode;
            this.commissionScheme = this.user.commissionScheme;
            this.advisorEmail = this.user.testator.advisor
              ? this.user.testator.advisor.email
              : null;
            if (!this.user.phone) {
              this.user.phone = this.user.testator.cellNumber;
            }
            this.loading = false;
          }
        },
      },
    },
  };
</script>
